var React = require('react');
const {
  getCropTypePrefix,
  DefaultGDD,
  DefaultYield,
} = require('./CropManagement.util');
const { useState, useEffect } = require('react');
const { useTranslation } = require('react-i18next');
var { upper } = require('@managers/helpers');
var cropDataManager = require('cropDataManager');
var CSS = require('../crop/CropManagement.scss');

const EditCropSection = ({ crops, distributions, onUpdate, showToast }) => {
  const [gddParameters, setGddParameters] = useState(DefaultGDD);
  const [fathersGddParameters, setFathersGddParameters] = useState(DefaultGDD);
  const [yieldParameters, setYieldParameters] = useState(DefaultYield);
  const [fathersYieldParameters, setFathersYieldParameters] =
    useState(DefaultYield);
  const [phenologicalPhases, setPhenologicalPhases] = useState([]);
  const [cropType, setCropType] = useState('');
  const [cropVariety, setCropVariety] = useState('');
  const [cropFeature, setCropFeature] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [matchingFields, setMatchingFields] = useState([]);
  const { t } = useTranslation();

  // Find fields that match the exact selected crop type/variety/feature
  const findMatchingFields = (type, variety, feature) => {
    if (!distributions || !type) {
      setMatchingFields([]);
      return;
    }

    // distributions is a Distributions class object, actual data is in .distributions
    const distributionsData = distributions.distributions;
    if (!distributionsData || typeof distributionsData !== 'object') {
      setMatchingFields([]);
      return;
    }

    const matches = [];

    // Normalize the selected values for comparison
    const selectedType = type.toLowerCase();
    const selectedVariety = (variety || 'general').toLowerCase();
    const selectedFeature = (feature || 'general').toLowerCase();

    for (const distKey in distributionsData) {
      const distribution = distributionsData[distKey];

      if (!distribution?.farming_units) {
        continue;
      }

      for (const fuKey in distribution.farming_units) {
        const farmingUnit = distribution.farming_units[fuKey];

        if (!farmingUnit?.fields) {
          continue;
        }

        for (const fieldKey in farmingUnit.fields) {
          const field = farmingUnit.fields[fieldKey];

          // CropData has separate properties: crop_type, crop_variety, crop_feature
          const fieldType = field?.crop_data?.crop_type?.toLowerCase() || '';
          const fieldVariety = (
            field?.crop_data?.crop_variety || 'general'
          ).toLowerCase();
          const fieldFeature = (
            field?.crop_data?.crop_feature || 'general'
          ).toLowerCase();

          // Exact match - all three properties must match
          if (
            fieldType === selectedType &&
            fieldVariety === selectedVariety &&
            fieldFeature === selectedFeature
          ) {
            matches.push({
              distributionName: distribution.name,
              farmingUnitName: farmingUnit.name,
              fieldName: field.name,
            });
          }
        }
      }
    }

    setMatchingFields(matches);
  };

  // Update matching fields when crop selection changes
  useEffect(() => {
    findMatchingFields(cropType, cropVariety, cropFeature);
  }, [cropType, cropVariety, cropFeature, distributions?.distributions]);

  const getMainCropParameters = (type, crop_type = cropType) => {
    let firstVariety = Object.keys(crops[crop_type]).includes('general')
      ? 'general'
      : Object.keys(crops[crop_type])[0];
    let firstFeature = Object.keys(crops[crop_type][firstVariety]).includes(
      'general',
    )
      ? 'general'
      : Object.keys(crops[crop_type][firstVariety])[0];
    if (crops[crop_type][firstVariety][firstFeature][type]) {
      return crops[crop_type][firstVariety][firstFeature][type];
    } else return [];
  };

  const handlePhenologicalPhases = (phenologicalPhasesFromDB, crop_type) => {
    if (Array.isArray(phenologicalPhasesFromDB)) {
      setPhenologicalPhases(phenologicalPhasesFromDB);
      return;
    }
    const mainCropPhases = getMainCropParameters(
      'phenological_phases',
      crop_type,
    );
    setPhenologicalPhases(mainCropPhases || []);
  };

  const handleYieldParametersChange = (yieldParamsFromDB) => {
    if (yieldParamsFromDB) {
      setYieldParameters(yieldParamsFromDB);
    } else {
      setYieldParameters(DefaultYield);
    }
  };

  const handleFathersGddParametersChange = (
    fathersGddParams,
    extend = true,
  ) => {
    if (fathersGddParams) {
      setFathersGddParameters(
        extend
          ? { ...fathersGddParameters, ...fathersGddParams }
          : fathersGddParams,
      );
    } else {
      setFathersGddParameters(DefaultGDD);
    }
  };

  const handleFathersYieldParametersChange = (
    fathersYieldParams,
    extend = true,
  ) => {
    if (fathersYieldParams) {
      setFathersYieldParameters(
        extend
          ? { ...fathersYieldParameters, ...fathersYieldParams }
          : fathersYieldParams,
      );
    } else {
      setFathersYieldParameters(DefaultYield);
    }
  };

  const handleCropTypeChange = (crop) => {
    setCropType(crop);
    let firstVariety = Object.keys(crops[crop]).includes('general')
      ? 'general'
      : Object.keys(crops[crop])[0];
    let firstFeature = Object.keys(crops[crop][firstVariety]).includes(
      'general',
    )
      ? 'general'
      : Object.keys(crops[crop][firstVariety])[0];
    setCropVariety(firstVariety);
    setCropFeature(firstFeature);
    if (firstVariety === 'general' && firstFeature === 'general') {
      handleFathersGddParametersChange(
        crops[crop][firstVariety][firstFeature]['gdd'],
        false,
      );
      handleFathersYieldParametersChange(
        crops[crop][firstVariety][firstFeature]['yield'],
        false,
      );
    }

    handleGDDParametersChange(crops[crop][firstVariety][firstFeature]['gdd']);
    handlePhenologicalPhases(
      crops[crop][firstVariety][firstFeature]['phenological_phases'],
      crop,
    );
    handleYieldParametersChange(
      crops[crop][firstVariety][firstFeature]['yield'],
    );
  };

  const handleCropVarietyChange = (variety) => {
    setCropVariety(variety);
    let firstFeature = Object.keys(crops[cropType][variety])[0];
    if ('general' in crops[cropType][variety]) {
      firstFeature = 'general';
    }

    setCropFeature(firstFeature);

    const typeGeneralGeneral = crops[cropType]?.general?.general;
    handleFathersGddParametersChange(typeGeneralGeneral?.gdd, false);
    handleFathersYieldParametersChange(typeGeneralGeneral?.yield, false);
    handleGDDParametersChange(crops[cropType][variety][firstFeature]['gdd']);
    handlePhenologicalPhases(
      crops[cropType][variety][firstFeature]['phenological_phases'],
      cropType,
    );
    handleYieldParametersChange(
      crops[cropType][variety][firstFeature]['yield'],
    );
  };

  const handleCropFeatureChange = (feature) => {
    setCropFeature(feature);
    let gdd_to_inherit = {};
    let yield_to_inherit = {};
    const typeGeneralGeneral = crops[cropType]?.general?.general;
    const varietyGeneral = crops[cropType]?.[cropVariety]?.general;
    const generalVarietyNode = crops[cropType]?.general;
    if (feature === 'general') {
      gdd_to_inherit = {
        ...(typeGeneralGeneral?.gdd || {}),
        ...(varietyGeneral?.gdd || {}),
      };
      yield_to_inherit = {
        ...(typeGeneralGeneral?.yield || {}),
        ...(varietyGeneral?.yield || {}),
      };
      handleFathersGddParametersChange(gdd_to_inherit, false);
      handleFathersYieldParametersChange(yield_to_inherit, false);
    } else {
      if (
        generalVarietyNode &&
        typeof generalVarietyNode === 'object' &&
        Object.prototype.hasOwnProperty.call(generalVarietyNode, feature)
      ) {
        const featureUnderGeneralVariety = generalVarietyNode[feature];
        gdd_to_inherit = { ...(featureUnderGeneralVariety?.gdd || {}) };
        yield_to_inherit = {
          ...(featureUnderGeneralVariety?.yield || {}),
        };
      }
      handleFathersGddParametersChange(
        {
          ...gdd_to_inherit,
          ...(varietyGeneral?.gdd || {}),
        },
        false,
      );
      handleFathersYieldParametersChange(
        {
          ...yield_to_inherit,
          ...(varietyGeneral?.yield || {}),
        },
        false,
      );
    }

    handleGDDParametersChange(crops[cropType][cropVariety][feature]['gdd']);
    handlePhenologicalPhases(
      crops[cropType][cropVariety][feature]['phenological_phases'],
      cropType,
    );
    handleYieldParametersChange(crops[cropType][cropVariety][feature]['yield']);
  };

  const handleUpdate = () => {
    let crop_type = '';

    if (
      cropType &&
      cropVariety &&
      cropVariety !== 'general' &&
      cropFeature &&
      cropFeature !== 'general'
    ) {
      const prefix = getCropTypePrefix(
        crops,
        cropType,
        cropVariety,
        cropFeature,
      );
      if (prefix) {
        crop_type = prefix.replace(/#/g, '%23') + '%24' + cropFeature;
      } else {
        // Fallback: construct the crop_type from scratch
        crop_type = cropType + '%23' + cropVariety + '%24' + cropFeature;
      }
    } else if (
      cropType &&
      cropVariety &&
      cropVariety !== 'general' &&
      cropFeature === 'general'
    ) {
      const prefix = getCropTypePrefix(
        crops,
        cropType,
        cropVariety,
        cropFeature,
      );
      if (prefix) {
        crop_type = prefix.replace(/#/g, '%23');
      } else {
        // Fallback: construct the crop_type from scratch
        crop_type = cropType + '%23' + cropVariety;
      }
    } else if (
      cropType &&
      (cropVariety === 'general' || cropVariety === '') &&
      cropFeature &&
      cropFeature !== 'general'
    ) {
      crop_type = cropType + '%24' + cropFeature;
    } else if (
      cropType &&
      (cropVariety === 'general' || cropVariety === '') &&
      (cropFeature === 'general' || cropFeature === '')
    ) {
      crop_type = cropType;
    }

    try {
      const filteredGddParameters = Object.entries(gddParameters).reduce(
        (acc, [key, value]) => {
          if (value !== '') {
            acc[key] = parseFloat(value);
          }
          return acc;
        },
        {},
      );

      const filteredYieldParameters = Object.entries(yieldParameters).reduce(
        (acc, [key, value]) => {
          if (value !== '') {
            acc[key] = parseFloat(value);
          }
          return acc;
        },
        {},
      );

      // Process phenological phases - convert numeric strings to numbers
      const processedPhases = phenologicalPhases.map((phase) => ({
        name: phase.name || '',
        duration_days:
          phase.duration_days !== '' ? parseFloat(phase.duration_days) : '',
        duration_gdd:
          phase.duration_gdd !== '' ? parseFloat(phase.duration_gdd) : '',
        kc: phase.kc !== '' ? parseFloat(phase.kc) : '',
        is_dev: Boolean(phase.is_dev),
        stress: {
          is_stress_active: Boolean(phase.stress?.is_stress_active),
          factor:
            phase.stress?.factor !== '' ? parseFloat(phase.stress?.factor) : '',
        },
      }));

      const updatedCropItem = {
        id: crop_type,
        body: {
          gdd: filteredGddParameters,
          yield: filteredYieldParameters,
          phenological_phases: processedPhases,
        },
      };
      cropDataManager
        .updateCropItem(updatedCropItem)
        .then((res) => {
          if (res['success']) {
            onUpdate();
            showToast(
              'success',
              t('crop_updated_successfully') || 'Crop updated successfully',
            );
          }
        })
        .catch((err) => {
          console.error('Error updating crop item:', err);
          showToast(
            'error',
            t('crop_update_failed') || 'Failed to update crop',
          );
        });
      if (typeof mixpanel !== 'undefined') {
        mixpanel.track('Crop DB Update', {
          'Crop ID': crop_type,
          'Crop Name': cropType,
        });
      }
    } catch (error) {
      console.error('Error updating crop item:', error);
    }
  };

  const handleGDDParametersChange = (gddParamsFromDB) => {
    if (gddParamsFromDB) {
      setGddParameters(gddParamsFromDB);
    } else {
      setGddParameters(DefaultGDD);
    }
  };

  const handleAddPhase = () => {
    setPhenologicalPhases([
      ...phenologicalPhases,
      {
        name: '',
        duration_days: '',
        kc: '',
        stress: {
          factor: '',
          is_stress_active: false,
        },
        duration_gdd: '',
        is_dev: false,
      },
    ]);
  };

  const handlePhaseChange = (index, field, value) => {
    const newPhenologicalPhases = [...phenologicalPhases];
    if (
      field === 'is_stress_active' &&
      newPhenologicalPhases[index].hasOwnProperty('stress')
    ) {
      newPhenologicalPhases[index]['stress']['is_stress_active'] = value;
    } else if (
      field === 'factor' &&
      newPhenologicalPhases[index].hasOwnProperty('stress')
    ) {
      newPhenologicalPhases[index]['stress']['factor'] = value;
    } else {
      newPhenologicalPhases[index][field] = value;
    }
    setPhenologicalPhases(newPhenologicalPhases);
  };

  const handleDeletePhase = (index) => {
    setPhenologicalPhases((prevPhases) => {
      const newPhases = [...prevPhases];
      newPhases.splice(index, 1);
      return newPhases;
    });
  };

  const renderPhaseField = (phase, index, fieldKey) => {
    const isStressField =
      fieldKey === 'is_stress_active' || fieldKey === 'factor';
    const value = isStressField
      ? fieldKey === 'factor'
        ? phase.stress?.factor
        : phase.stress?.is_stress_active
      : phase[fieldKey];

    const labelMap = {
      name: 'Name',
      duration_days: 'Duration (Days)',
      kc: 'KC',
      duration_gdd: 'Duration (GDD)',
      is_dev: 'Is Development',
      is_stress_active: 'Stress Active',
      factor: 'Stress Factor',
    };

    if (fieldKey === 'is_dev' || fieldKey === 'is_stress_active') {
      return (
        <div className='phase-field' key={fieldKey}>
          <label>{labelMap[fieldKey]}</label>
          <select
            value={value}
            onChange={(e) =>
              handlePhaseChange(index, fieldKey, e.target.value === 'true')
            }
          >
            <option value={true}>True</option>
            <option value={false}>False</option>
          </select>
        </div>
      );
    }

    return (
      <div className='phase-field' key={fieldKey}>
        <label>{labelMap[fieldKey]}</label>
        <input
          type='text'
          value={value || ''}
          onChange={(e) => handlePhaseChange(index, fieldKey, e.target.value)}
        />
      </div>
    );
  };

  return (
    <div className='crop-section-card'>
      <div
        className='crop-section-header'
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h2>
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#4563c7'
            strokeWidth='2'
          >
            <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
            <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
          </svg>
          {t('edit_parameters') || 'Edit Parameters'}
        </h2>
        <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>
          ▼
        </span>
      </div>

      {!isCollapsed && (
        <div className='crop-section-content'>
          {/* Crop Selection */}
          <div className='form-row-group'>
            <div className='form-field'>
              <label>{t('crop_type')}</label>
              <select onChange={(e) => handleCropTypeChange(e.target.value)}>
                <option value=''>{t('select_crop') || 'Select crop...'}</option>
                {crops &&
                  Object.keys(crops)
                    .sort()
                    .map((item) => (
                      <option key={item} value={item}>
                        {t(upper(item))}
                      </option>
                    ))}
              </select>
            </div>

            <div className='form-field'>
              <label>{t('kc_variety')}</label>
              <select
                value={cropVariety}
                onChange={(e) => handleCropVarietyChange(e.target.value)}
                disabled={!cropType}
              >
                {crops &&
                  cropType &&
                  Object.keys(crops[cropType])
                    .sort()
                    .map((item) => (
                      <option key={item} value={item}>
                        {t(upper(item))}
                      </option>
                    ))}
              </select>
            </div>

            <div className='form-field'>
              <label>{t('kc_feature')}</label>
              <select
                value={cropFeature}
                onChange={(e) => handleCropFeatureChange(e.target.value)}
                disabled={!cropType || !cropVariety}
              >
                {crops &&
                  cropType &&
                  cropVariety &&
                  Object.keys(crops[cropType][cropVariety])
                    .sort()
                    .map((key) => (
                      <option key={key} value={key}>
                        {t(upper(key))}
                      </option>
                    ))}
              </select>
            </div>
          </div>

          {cropType && (
            <>
              {/* Phenological Phases Section */}
              <div className='section-divider' />
              <div className='subsection-title'>
                {t('phenological_phases') || 'Phenological Phases'}
              </div>

              <div className='phases-container'>
                {phenologicalPhases.map((phase, index) => (
                  <div key={index} className='phase-card'>
                    <div className='phase-header'>
                      <span className='phase-title'>
                        {t('phase') || 'Phase'} {index + 1}
                      </span>
                      <button
                        className='btn-danger-crop'
                        onClick={() => handleDeletePhase(index)}
                        style={{ padding: '4px 8px', fontSize: '11px' }}
                      >
                        {t('delete') || 'Delete'}
                      </button>
                    </div>
                    <div className='phase-fields'>
                      {renderPhaseField(phase, index, 'name')}
                      {renderPhaseField(phase, index, 'kc')}
                      {renderPhaseField(phase, index, 'duration_days')}
                      {renderPhaseField(phase, index, 'duration_gdd')}
                      {renderPhaseField(phase, index, 'is_dev')}
                      {renderPhaseField(phase, index, 'is_stress_active')}
                      {renderPhaseField(phase, index, 'factor')}
                    </div>
                  </div>
                ))}

                <div className='add-phase-card' onClick={handleAddPhase}>
                  <span>+ {t('add_phase') || 'Add Phase'}</span>
                </div>
              </div>

              {/* GDD Parameters Section */}
              <div className='section-divider' />
              <div className='subsection-title'>
                {t('gdd_parameters') || 'GDD Parameters'}
              </div>

              <div className='parameters-grid'>
                {Object.keys(DefaultGDD).map((key) => (
                  <div className='parameter-card' key={key}>
                    <label>{key}</label>
                    <input
                      type='number'
                      value={key in gddParameters ? gddParameters[key] : ''}
                      placeholder={
                        key in fathersGddParameters
                          ? `${t('inherited') || 'Inherited'}: ${fathersGddParameters[key]}`
                          : ''
                      }
                      onChange={(e) =>
                        setGddParameters({
                          ...gddParameters,
                          [key]: e.target.value,
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              {/* Yield Parameters Section */}
              <div className='section-divider' />
              <div className='subsection-title'>
                {t('yield_parameters') || 'Yield Parameters'}
              </div>

              <div className='parameters-grid'>
                {Object.keys(DefaultYield).map((key) => (
                  <div className='parameter-card' key={key}>
                    <label>{key}</label>
                    <input
                      type='number'
                      value={key in yieldParameters ? yieldParameters[key] : ''}
                      placeholder={
                        key in fathersYieldParameters
                          ? `${t('inherited') || 'Inherited'}: ${fathersYieldParameters[key]}`
                          : ''
                      }
                      onChange={(e) =>
                        setYieldParameters({
                          ...yieldParameters,
                          [key]: e.target.value,
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className='action-buttons'>
                <button className='btn-primary-crop' onClick={handleUpdate}>
                  {t('update') || 'Update'}
                </button>
              </div>

              {/* Matching Fields Section */}
              <div className='section-divider' />
              <div className='matching-fields-section'>
                <div className='section-header'>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#4563c7'
                    strokeWidth='2'
                  >
                    <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
                    <circle cx='12' cy='10' r='3' />
                  </svg>
                  <span className='section-title'>
                    {t('fields_using_crop') || 'Fields Using This Crop'}
                  </span>
                  <span className='field-count-badge'>
                    {matchingFields.length}
                  </span>
                </div>
                <div className='section-hint'>
                  {t('permission_hint') ||
                    'Only showing fields you have permission to view'}
                </div>
                <div className='fields-grid'>
                  {matchingFields.length > 0 ? (
                    matchingFields.map((field, index) => (
                      <div key={index} className='field-item-card'>
                        <div className='field-name'>{field.fieldName}</div>
                        <div className='field-location'>
                          <span className='distribution-name'>
                            {field.distributionName}
                          </span>
                          <span className='separator'>/</span>
                          <span className='farming-unit-name'>
                            {field.farmingUnitName}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className='no-fields-message'>
                      <p>
                        {t('no_fields_found') ||
                          'No fields found using this crop configuration'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {!cropType && (
            <div className='empty-state'>
              <p>{t('select_crop_hint')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

module.exports = EditCropSection;
